// JSON-LD schema builders. Each returns a plain object; feed to useSEO({ jsonld: [...] }).
import { SITE_URL, BUSINESS } from "@/data/site";

const abs = (path = "") => {
  if (!path) return SITE_URL;
  if (/^https?:/.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

// ---- Site-wide -------------------------------------------------------

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Electrician",
    "@id": `${SITE_URL}/#business`,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    url: SITE_URL,
    logo: abs(BUSINESS.logo),
    image: abs(BUSINESS.logo),
    telephone: BUSINESS.phoneE164,
    email: BUSINESS.email,
    priceRange: BUSINESS.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.streetAddress,
      addressLocality: BUSINESS.addressLocality,
      addressRegion: BUSINESS.addressRegion,
      postalCode: BUSINESS.postalCode,
      addressCountry: BUSINESS.addressCountry,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.latitude,
      longitude: BUSINESS.longitude,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "07:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
        description: "24/7 Emergency Response",
      },
    ],
    areaServed: BUSINESS.areasServed.map((city) => ({
      "@type": "City",
      name: city,
    })),
    aggregateRating: BUSINESS.aggregateRating && {
      "@type": "AggregateRating",
      ratingValue: BUSINESS.aggregateRating.ratingValue,
      reviewCount: BUSINESS.aggregateRating.reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
    sameAs: BUSINESS.socialSameAs,
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: BUSINESS.name,
    publisher: { "@id": `${SITE_URL}/#business` },
    inLanguage: "en-CA",
  };
}

// ---- Per-page helpers ------------------------------------------------

export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: abs(c.href),
    })),
  };
}

export function faqSchema(faqs) {
  if (!faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export function serviceSchema({ name, description, url, image, areaServed, serviceType, offersUrl }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: abs(url),
    image: image ? abs(image) : abs(BUSINESS.logo),
    provider: { "@id": `${SITE_URL}/#business` },
    serviceType: serviceType || name,
    areaServed: (areaServed || BUSINESS.areasServed).map((city) => ({
      "@type": "City",
      name: city,
    })),
    ...(offersUrl && {
      offers: {
        "@type": "Offer",
        url: abs(offersUrl),
        availability: "https://schema.org/InStock",
      },
    }),
  };
}

export function articleSchema({ headline, description, url, image, datePublished, dateModified, author, section }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    mainEntityOfPage: { "@type": "WebPage", "@id": abs(url) },
    url: abs(url),
    image: image ? [abs(image)] : [abs(BUSINESS.logo)],
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Person",
      name: author || BUSINESS.name,
    },
    publisher: {
      "@type": "Organization",
      name: BUSINESS.name,
      logo: { "@type": "ImageObject", url: abs(BUSINESS.logo) },
    },
    ...(section && { articleSection: section }),
  };
}

export function blogPostingSchema(post) {
  return {
    ...articleSchema({
      headline: post.title,
      description: post.excerpt,
      url: `/blog/${post.slug}`,
      image: post.cover,
      datePublished: post.date,
      author: post.author,
      section: post.tags?.[0],
    }),
    "@type": "BlogPosting",
  };
}

export function caseStudySchema(project) {
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.title,
    description: project.description,
    url: abs(`/projects/${project.slug}`),
    image: project.featureImage ? [abs(project.featureImage)] : [abs(BUSINESS.logo)],
    author: { "@id": `${SITE_URL}/#business` },
    locationCreated: project.location && {
      "@type": "Place",
      name: project.location,
    },
    keywords: (project.tags || []).join(", "),
    dateCreated: [project.completedMonth, project.completedYear].filter(Boolean).join(" "),
    ...(project.review?.quote && {
      review: {
        "@type": "Review",
        reviewBody: project.review.quote,
        author: { "@type": "Person", name: project.review.name || "Customer" },
        reviewRating: project.review.rating && {
          "@type": "Rating",
          ratingValue: project.review.rating,
          bestRating: 5,
        },
      },
    }),
  };
}
